import { cva, type VariantProps } from 'class-variance-authority';

import { WEIGHT_TO_CLASSES } from '../../styles/typography';

/**
 * Bottom-sheet visual variants — mirror Figma "Flashka Design System" file
 * `YZK9r3wDhvw1pY60KlVEIj`, frame **5456:48693 "Bottom sheet"**. The frame is a
 * 4 × 7 × 2 matrix (`Header type` × `Footer type` × `Alignement`). Reference
 * nodes read for this implementation:
 *
 *   - 5456:48691 – Title            / Single button                  / Left
 *   - 5459:48694 – Title            / Double button "Horixontal"     / Center
 *   - 5456:48681 – Header +back     / None                           / Left
 *   - 5456:48682 – Header +image    / Double button "Horixontal"     / Left
 *   - 5456:48685 – No header        / Double button "vertically"     / Left
 *   - 5475:73545 – Title            / Overlayered button             / Left
 *
 * ## Figma's footer axis is mis-named
 *
 * "Double button **Horixontal** aligned" is a COLUMN of two full-width buttons
 * (5456:48688 is 56px taller than its sibling), and "Double button
 * **vertically** aligned" is a ROW of two half-width buttons. The code calls
 * them `stacked` and `inline` — see `BottomSheetFooterLayout`.
 *
 * ## The seven Figma footer types collapse to two props
 *
 * `None` / `Single button` / `Double …` are just "how many actions did the
 * caller pass" × `footerLayout`; the three `Overlayered …` types are the same
 * three footers with `footerOverlay` on. So the component derives the footer
 * from `primaryAction` / `secondaryAction` / `footerLayout` / `footerOverlay`
 * instead of exposing a seven-valued enum. The header axis is derived the same
 * way from `image` / `onBack` / `title`.
 *
 * ## Anatomy (top → bottom)
 *
 *   [handle] – [header] – [body (media slot) – pagination?] – [footer – footer text?]
 *
 * ## Surface
 *
 * Figma draws the sheet as a DETACHED, fully-rounded card (`rounded-[36px]` on
 * all four corners, a 1px border all the way round, and a drop shadow) — not a
 * panel flush with the screen edge. `BOTTOM_SHEET_SCREEN_INSET_PX` is the gap
 * the shells leave around it (the component is 380 wide on a 393pt frame).
 *
 * The fill is Figma's `brand/secondary/130` (#f5f5f5b2) → `bg-secondary-100/70`,
 * and the "Glass" effect (radius 27) → `web:backdrop-blur-[27px]` on web plus an
 * expo-blur `BlurView` on native (the shared `GlassBackground`). Identical
 * treatment to the Toast card. No `dark:` overrides anywhere: the shared palette
 * auto-inverts every shade in dark mode.
 *
 * ## Composed, not re-built
 *
 * Every button in the sheet is the shared {@link Button}, so bevels, pressed
 * states, focus rings and disabled tones are inherited:
 *   footer buttons  → `xxl` (56px, radii 21/22)  primary / secondary
 *   back button     → `secondary` `m` icon-only  (40px, radii 17/18)
 *   image ✕ button  → `secondary` `s` icon-only  (36px, radii 15/16)
 *   header ✕ button → `tertiary`  `s` icon-only  (36px, ghost — Figma's plain
 *                     36px `rounded-[14px]` box, ±1px on the radius)
 * The pagination indicator is the shared `PaginationDots` (`primary`).
 */

/** Gap Figma leaves between the sheet card and the screen edges, in px. */
export const BOTTOM_SHEET_SCREEN_INSET_PX = 8;

/** Sheet corner radius in px (Figma `rounded-[36px]`). */
export const BOTTOM_SHEET_RADIUS_PX = 36;

/** Media-slot / image-header corner radius in px (Figma `rounded-[28px]`). */
export const BOTTOM_SHEET_MEDIA_RADIUS_PX = 28;

/** Default image-header height in px (Figma "Image Header" = 242). */
export const BOTTOM_SHEET_IMAGE_HEIGHT_PX = 242;

/** Blur radius of Figma's "Glass" effect on the sheet surface. */
export const BOTTOM_SHEET_GLASS_BLUR_PX = 27;

/**
 * The dimming backdrop behind a presented sheet — gorhom's own defaults,
 * NAMED because two layers have to agree on them.
 *
 * The backdrop renders in gorhom's PORTAL, which is outside the
 * `GlassBlurTarget` that Android's frost samples. So the Android card has to
 * re-paint this dim itself (see `CardChromeBackground`) or it frosts the
 * undimmed page and reads far brighter than the backdrop directly above it.
 * Passed explicitly to `BottomSheetBackdrop` rather than left to its defaults,
 * so the card's stand-in cannot silently drift from the real thing.
 */
export const BOTTOM_SHEET_BACKDROP_OPACITY = 0.5;
export const BOTTOM_SHEET_BACKDROP_COLOR = '#000';

/**
 * Blur radius of the Footer Overlay backdrop. Figma 5553:114666 says 18, but
 * that read too heavy in review — lightened, and applied to the footer's
 * CONTENT box (see {@link bottomSheetFooterOverlayContentClass}) rather than
 * the full-width band.
 */
export const BOTTOM_SHEET_FOOTER_BLUR_PX = 12;

/** 1px border on every side of the card — matters when insetting the glass clip. */
export const BOTTOM_SHEET_BORDER_WIDTH = 1;

/**
 * Gap kept between the open keyboard and whatever floats above it — the whole
 * card (`keyboardApproach: 'shift-sheet'`) or the hoisted footer
 * (`'float-footer'`). gorhom parks both EXACTLY flush on the keyboard's top
 * edge; the shells nudge them by this much (see the native shell).
 */
export const BOTTOM_SHEET_KEYBOARD_GAP_PX = 8;

/**
 * How far the hoisted footer's content drops toward the keyboard while it is
 * open. The footer band's resting bottom padding is 12px
 * ({@link bottomSheetFooterOverlayClass}); dropping the content by the
 * difference turns that into the DS's 8px keyboard gap. A transform, not a
 * padding flip: padding would feed back into gorhom's footer measurement, and
 * a React re-render may not even happen mid-keyboard (the footer container is
 * memoised — only worklets are guaranteed to run).
 */
export const BOTTOM_SHEET_FOOTER_KEYBOARD_DROP_PX = 12 - BOTTOM_SHEET_KEYBOARD_GAP_PX;

/**
 * Resting drop of the `'float-footer'` slot copy. The copy hands off to the
 * card's IN-FLOW footer (buttons 12px above the card bottom — the card's
 * `pb-[12px]`), and the overlay band pads the same 12px, so the copy's rest
 * pose already pixel-matches the footer it covers. Kept as a named 0 so the
 * two numbers stay tied: if either changes, the difference is the drop.
 */
export const BOTTOM_SHEET_FOOTER_REST_ALIGN_DROP_PX = 12 - 12;

/** Default duration of the multi-step slide, in ms. */
export const DEFAULT_BOTTOM_SHEET_STEP_DURATION_MS = 260;

/**
 * The sheet card's translucent fill — Figma `brand/secondary/130` (#f5f5f5b2).
 * Named because THREE layers have to agree on it: the web card, the native
 * chrome, and the tint the native glass layer re-paints over its frost on
 * Android (see `GlassBackground.native`).
 */
export const bottomSheetCardFillClass = 'bg-surface-overlay/70';

/**
 * Painted OVER {@link bottomSheetCardFillClass} on an Android device that
 * cannot frost (no `GlassBlurTarget`, or SDK < 31). The two composite to
 * `1 - 0.3 * 0.2 = 0.94` — still a tinted surface rather than a flat block,
 * but page content no longer reads through the card. Same trade, and the same
 * arithmetic, as the anchored menus' Android fallback.
 */
export const bottomSheetCardNoFrostFillClass = 'bg-surface-overlay/80';

/**
 * The sheet card. `pb` tracks the footer: Figma pads 24px under an in-flow
 * footer (5456:48682 / 48685 / 48691) and 12px when there is none
 * (5456:48681) — the overlay footer brings its own 28px bottom padding, so
 * the card falls back to the bare 12px there too.
 */
export const bottomSheetVariants = cva(
  // NB: the 12px horizontal inset Figma draws on the card is applied
  // PER-REGION (`bottomSheetRegionPadClass`), not here, so a `padding="none"`
  // body can reach the card's edges — full-bleed viewers (PDF, WebView, image)
  // need that, and a card-level `px` would letterbox them.
  //
  // `pt-[12px]`: Figma's node draws the handle 8px from the card's top edge,
  // but the DS overrides it to 12 so EVERY gap in the card's top chrome is the
  // same 12 (top → handle → content) — decided 2026-08-13.
  'relative flex flex-col items-center gap-[12px] w-full overflow-hidden ' +
    `rounded-sheet border border-secondary-200 ${bottomSheetCardFillClass} ` +
    'pt-[12px] ' +
    'web:shadow-sheet web:backdrop-blur-[27px]',
  {
    variants: {
      hasInFlowFooter: {
        // Even on every side (design review 2026-09): 12px under an in-flow
        // footer too, the same as the card's side and top insets.
        true: 'pb-[12px]',
        false: 'pb-[12px]',
      },
      compact: {
        true: 'gap-[6px] pt-[6px]',
        false: '',
      },
    },
    defaultVariants: { hasInFlowFooter: false, compact: false },
  },
);

/** Figma's 12px horizontal card inset, applied per region — see the CVA note. */
export const bottomSheetRegionPadClass = 'px-[12px]';

/**
 * The Surface's card when the NATIVE shell hosts the chrome elsewhere —
 * gorhom's `backgroundComponent` paints fill/border/radius/glass/shadow and
 * its `handleComponent` owns the top strip (inset + pill + gap), so the
 * Surface is a bare column: regions, gaps and the bottom padding only.
 * `rounded-b-sheet overflow-hidden` still clips body content to the card's
 * bottom corners. `bareTop` re-adds the 12px top inset for sheets whose
 * surface starts at the card's top (`showHandle={false}`, or the invisible
 * `header={false}` strip the surface is pulled up under) —
 * and, since the surface then IS the card, rounds the top corners so a
 * full-bleed body is clipped to the card's radius on all four.
 */
export const bottomSheetHostedSurfaceVariants = cva(
  'relative flex w-full flex-col items-center overflow-hidden rounded-b-sheet',
  {
    variants: {
      hasInFlowFooter: {
        // Even on every side (design review 2026-09): 12px under an in-flow
        // footer too, the same as the card's side and top insets.
        true: 'pb-[12px]',
        false: 'pb-[12px]',
      },
      compact: {
        true: 'gap-[6px]',
        false: 'gap-[12px]',
      },
      bareTop: {
        true: 'pt-[12px] rounded-t-sheet',
        false: '',
      },
    },
    compoundVariants: [{ compact: true, bareTop: true, class: 'pt-[6px]' }],
    defaultVariants: { hasInFlowFooter: false, compact: false, bareTop: false },
  },
);

/**
 * Drag handle pill geometry — Figma "Handle": 55 × 4, `rounded-[8px]`.
 * THE handle spec for every sheet-like surface: the DS BottomSheet draws it
 * via {@link bottomSheetHandleClass}; Select.native's option sheet and
 * DropdownMenu's `withHandle` pill consume the constants so no sheet ships a
 * wider (or narrower) handle again.
 */
export const BOTTOM_SHEET_HANDLE_WIDTH_PX = 55;
export const BOTTOM_SHEET_HANDLE_HEIGHT_PX = 4;
export const BOTTOM_SHEET_HANDLE_RADIUS_PX = 8;

/**
 * Height of the native shell's REAL gorhom handle strip: the card's top
 * chrome, 12 (inset) + 4 (pill) + 12 (gap before the content) — or halved
 * insets in `compact`. On native the strip is a true gorhom `handleComponent`
 * (so the pan gesture is the library's own); the Surface then renders
 * chrome-less below it. See the native shell.
 */
export const BOTTOM_SHEET_HANDLE_STRIP_HEIGHT_PX =
  12 + BOTTOM_SHEET_HANDLE_HEIGHT_PX + 12;
export const BOTTOM_SHEET_HANDLE_STRIP_COMPACT_HEIGHT_PX =
  6 + BOTTOM_SHEET_HANDLE_HEIGHT_PX + 6;

/**
 * The card chrome WITHOUT layout — fill, border, radius — for the native
 * shell's gorhom `backgroundComponent`, which spans the whole sheet (handle
 * strip included) behind the content. Web keeps the composed
 * {@link bottomSheetVariants} card instead.
 */
export const bottomSheetCardChromeClass = `rounded-sheet border border-secondary-200 ${bottomSheetCardFillClass}`;

/**
 * The same chrome for an `attached` sheet — the card sits ON the screen edges,
 * so only the TOP corners round and the bottom border (which would land under
 * the home indicator) goes with them.
 */
export const bottomSheetCardAttachedChromeClass = `rounded-t-sheet border-x border-t border-secondary-200 ${bottomSheetCardFillClass}`;

/**
 * The card's fill at full strength — the `opaque` sheet, which draws no glass
 * at all. Same colour as {@link bottomSheetCardFillClass}, without the 70%:
 * the DS glass is tuned for content sitting on the media slot's own panel, so
 * a body placed straight on the card (the auth form) reads see-through, and on
 * a busy screen the art behind it shows through the copy.
 */
export const bottomSheetCardSolidFillClass = 'bg-surface-overlay';

/** The card chrome for a given shell: radius + border + fill, in one place. */
export function bottomSheetCardChrome({
  attached = false,
  opaque = false,
}: {
  attached?: boolean;
  opaque?: boolean;
} = {}): string {
  const shape = attached
    ? 'rounded-t-sheet border-x border-t border-secondary-200'
    : 'rounded-sheet border border-secondary-200';
  return `${shape} ${opaque ? bottomSheetCardSolidFillClass : bottomSheetCardFillClass}`;
}

/** Drag handle pill — Figma "Handle": 55 × 4, `rounded-[8px]`, secondary-400. */
export const bottomSheetHandleClass =
  'h-[4px] w-[55px] shrink-0 rounded-[8px] bg-secondary-400';

/**
 * Header row. Both kinds center their children vertically (5452:44672 is
 * `items-center`); the `back` kind adds Figma's 12px gap between the arrow and
 * the title group.
 *
 * ## The horizontal inset is COMPLETE here — don't add `bottomSheetRegionPadClass`
 *
 * Figma insets the header's LEADING edge past the card's 12px so the title
 * optically clears the body, while the trailing ✕ stays on the card inset:
 *
 *   title header (5452:44672)  frame x=12 + Title Group x=12 → **24** left, 12 right
 *   back header  (5443:44632)  frame x=12 + arrow x=8        → **20** left, 12 right
 *   centered     (5459:48694)  no extra pad                  → 12 both
 *
 * These are absolute insets from the card edge, because the card itself carries
 * no horizontal padding (see the CVA note above). Composing them with
 * `bottomSheetRegionPadClass` is what broke this before: `px-[12px]` and
 * `pl-[24px]` are the same tailwind-merge group, so the later one silently won
 * and every sheet title sat at 12px instead of 24px.
 */
export const bottomSheetHeaderVariants = cva('flex flex-row w-full items-center', {
  variants: {
    kind: {
      title: 'justify-between',
      back: 'gap-[12px]',
    },
    align: {
      left: '',
      center: '',
    },
    bordered: {
      true: 'border-b border-secondary-200 pb-[12px]',
      false: '',
    },
  },
  compoundVariants: [
    { kind: 'title', align: 'left', class: 'pl-[24px] pr-[12px]' },
    { kind: 'back', align: 'left', class: 'pl-[20px] pr-[12px]' },
    { kind: 'title', align: 'center', class: 'px-[12px]' },
    { kind: 'back', align: 'center', class: 'px-[12px]' },
  ],
  defaultVariants: { kind: 'title', align: 'left', bordered: false },
});

/**
 * The `closable` ✕, floated against the card: Figma puts the header's icon
 * button at y=24 with its right edge on the card's 12px inset (5452:44676), so
 * those are the offsets even when there is no header to hang it off.
 */
export const bottomSheetFloatingCloseClass = 'absolute right-[12px] top-[24px] z-10';

/**
 * Right padding a header needs so a long title clears the FLOATED ✕ — the
 * button is out of flow, so nothing else reserves the space. 12 (inset) + 36
 * (button) + 8 (gap).
 */
export const bottomSheetHeaderFloatingClosePadClass = 'pr-[56px]';

/** Title + subtitle column inside the header. */
export const bottomSheetTitleGroupVariants = cva(
  'flex flex-1 flex-col justify-center gap-[2px] min-w-0 self-stretch',
  {
    variants: {
      align: { left: '', center: 'text-center' },
    },
    defaultVariants: { align: 'left' },
  },
);

/**
 * Figma H2 — Mona Sans SemiBold 20/28, secondary-800.
 *
 * The generated `text-h2` token, NOT a raw `text-[20px] leading-[28px]`, and
 * the weight comes from {@link WEIGHT_TO_CLASSES}. Both halves matter:
 *
 * - **`native:font-mona-*` is what picks the weight on native.** NativeWind
 *   selects a Mona Sans weight by FAMILY (`font-mona-semibold` →
 *   `MonaSans-SemiBold`); a bare `font-semibold` leaves RN on the system face,
 *   faux-bolded. Every sheet title in the app rendered in SF Pro until this.
 * - **The token is rem, the raw px wasn't.** Type tokens get the
 *   responsive-rem treatment (1.25× on tablet / OS-large-text); `text-[20px]`
 *   is frozen, so the title alone stopped scaling with the sheet around it.
 */
export const bottomSheetTitleClass = `w-full text-h2 ${WEIGHT_TO_CLASSES.semibold} text-secondary-800`;

/** Sub text — Figma "Caption 1 Medium" (14/20), secondary-500. */
export const bottomSheetSubtitleClass = `w-full text-caption-1-medium ${WEIGHT_TO_CLASSES.medium} text-fg-subtle`;

/** Footer helper line — same type as the subtitle. */
export const bottomSheetFooterTextClass = bottomSheetSubtitleClass;

/**
 * Image header — a clipped, 28px-rounded frame. Figma stretches the image to
 * fill it and floats the ✕ button in the top-right with 8px of padding.
 *
 * Inset **12px** from the card on both sides — the same rhythm as every
 * other region (DS override of Figma 5456:48554's 22, which left the frame
 * visibly narrower than the content surface). It's a MARGIN, not padding:
 * padding would inset the artwork inside the rounded border instead of
 * moving the framed image itself.
 */
export const bottomSheetImageHeaderClass =
  'relative shrink-0 self-stretch mx-[12px] overflow-hidden rounded-sheet-media border border-secondary-200';

/** Wrapper pinning the ✕ over the image header. */
export const bottomSheetImageCloseWrapClass =
  'absolute inset-x-0 top-0 flex flex-row justify-end p-[8px]';

/**
 * Body + footer column. 12px gap when a footer follows the body — the same
 * "always 12px after the content" gap the card's `pb-[12px]` provides when
 * there is no footer.
 */
export const bottomSheetContentVariants = cva('flex w-full flex-col items-center', {
  variants: {
    hasInFlowFooter: { true: 'gap-[12px]', false: '' },
  },
  defaultVariants: { hasInFlowFooter: false },
});

/** Body slot + pagination indicator. */
export const bottomSheetBodyWrapClass = 'flex w-full flex-col items-center gap-[8px]';

/**
 * Figma "Media Slot" (5443:44621) — the sheet's CONTENT SURFACE, and a real
 * one: a translucent `brand/secondary/30` card (`secondary-50/70`, the same
 * token the secondary Button's glass fill uses, so it auto-inverts in dark
 * mode) with a 1px `secondary-200` border, a 28px radius, clipped contents and
 * **12px of internal padding**. See 5500:87000 for it filled with a menu —
 * that node is the three-dot folder menu, rows inside the card.
 *
 * `<BottomSheetContent>` draws this. Bare children stay unwrapped: the
 * full-bleed bodies (PDF, WebView, image viewer) must reach the card edges, and
 * a surface behind them would be a grey box nobody sees.
 */
/**
 * Dotted-fill geometry for `<BottomSheetContent variant="dotted">`.
 *
 * These are the PRODUCT-WIDE dot grid, not a per-component measurement: web's
 * `bg-dotted-pattern` + `bg-dotted-size` (flashka-web/tailwind.config.js) and
 * flashka-app's `DottedBackground` draw the same dot (`dotColor()`, one value per
 * theme) at r=1.5 on a 10px pitch; keep the geometry of all three in sync.
 *
 * They were previously read off a 1x render of Figma 5662:11360, whose Media
 * Slot pattern the codegen flattens away — that gave a 1px #e5e5e5 dot, both
 * smaller and a shade darker than the rest of the product, so the sheet's grid
 * read as a different texture next to a chat or a widget.
 */
export const BOTTOM_SHEET_DOT_PITCH_PX = 10;
/** Dot radius in px. */
export const BOTTOM_SHEET_DOT_RADIUS_PX = 1.5;

/**
 * The dotted variant swaps the translucent `secondary-30` fill for Figma's
 * flat `secondary-50` (#FAFAFA) base that the dots are drawn on. Dark: the
 * PAGE colour — the dotted texture is the canvas everywhere in the product
 * (web's chat is dotted `bg-background`), and on the raised #16161a the
 * chat, its recents and every control inside read one step too light
 * (review 2026-10: "the chat bg should be darker").
 */
export const bottomSheetMediaSlotDottedClass = 'bg-surface-raised dark:bg-background';

/** Height of the scroll-affordance fade at each edge of the content surface. */
export const CONTENT_FADE_HEIGHT = 24;

/**
 * Scroll-affordance fade over the content surface's top / bottom edge — web.
 * The ramp colour is `secondary-50/70`, the slot's own fill, so content
 * dissolves into the border rather than being sliced by it. `z-10` keeps it
 * over the scroller; `pointer-events-none` keeps it out of the way of taps.
 */
export const bottomSheetContentFadeVariants = cva(
  'pointer-events-none absolute inset-x-0 z-10 h-[24px]',
  {
    variants: {
      edge: {
        top: 'top-0 bg-gradient-to-b from-secondary-50/70 to-transparent',
        bottom: 'bottom-0 bg-gradient-to-t from-secondary-50/70 to-transparent',
      },
      // A dotted slot fades into ITS fill — the canvas in dark.
      dotted: { true: 'dark:from-background/70', false: '' },
    },
    defaultVariants: { edge: 'bottom', dotted: false },
  },
);

export const bottomSheetMediaSlotClass =
  // Column (the View primitive's default) with children stretching to full
  // width — Figma gets the same result via a row whose single child is
  // `flex-1 flex-col`, which doesn't translate to RN's default axis.
  'flex w-full flex-col overflow-hidden rounded-sheet-media ' +
  'border border-secondary-200 bg-surface-raised/70 p-[12px]';

/** Footer button group. */
export const bottomSheetFooterVariants = cva('flex w-full', {
  variants: {
    layout: {
      stacked: 'flex-col items-end justify-center gap-[8px]',
      inline: 'flex-row items-center justify-end gap-[8px]',
    },
  },
  defaultVariants: { layout: 'stacked' },
});

/**
 * Overlayered footer (Figma "Footer Overlay", node 5475:73533) — pinned to the
 * bottom of the card so the body scrolls beneath it. Web paints the spec'd
 * transparent→`secondary-50/80` gradient (stopping at 58%) behind an 18px
 * backdrop blur; native has no `bg-gradient-*` support in NativeWind v4, so it
 * falls back to the flat translucent fill.
 *
 * Used by the NATIVE shell's gorhom footer slot (`FooterBody`), which really
 * is absolutely positioned by gorhom. The Surface's own overlay footer uses
 * {@link bottomSheetFooterOverlayInFlowVariants} instead — see there for why.
 */
export const bottomSheetFooterOverlayClass =
  'absolute inset-x-0 bottom-0 flex flex-col gap-[10px] px-[12px] pt-[16px] pb-[12px] ' +
  'rounded-b-sheet overflow-hidden';

/**
 * The blurred backdrop hugs the footer's CONTENT (the button stack) instead
 * of the full-width band — a PURE backdrop blur, no fill of its own, so
 * whatever slides beneath simply frosts. The negative margin + equal padding
 * pair projects the blur past the buttons (a halo) without moving the buttons
 * themselves: `10px` to the sides and below, and up to the band's top edge
 * (its `pt-[16px]`) above, where {@link BOTTOM_SHEET_FOOTER_BLUR_RAMP_PX}
 * fades it in. `self-stretch` (not `w-full`) is what lets the negative
 * horizontal margins actually widen the box.
 * Wrap the footer content in this and put `BottomSheetFooterBackground`
 * inside it — it draws the blur on both platforms.
 */
export const bottomSheetFooterOverlayContentClass =
  'relative self-stretch overflow-hidden rounded-[24px] web:isolate ' +
  '-mx-[10px] -mb-[10px] -mt-[16px] px-[10px] pb-[10px] pt-[16px]';

/**
 * How tall the footer blur's fade-in is, from the band's top edge down. A
 * blur that starts at full strength draws a hard line across whatever row is
 * scrolling under the actions — sharp above, frosted below (design review
 * 2026-10, oral-exam setup: "replace the hard cut with a fade-out"). Ramping it
 * over the band's 16px top pad plus 10px into the first button dissolves the
 * row instead; the button's own fill hides the rest.
 */
export const BOTTOM_SHEET_FOOTER_BLUR_RAMP_PX = 26;

/**
 * Web twin of the native mask: the same ramp as a CSS `mask-image`. `-z-10`
 * inside the content box's `isolate` keeps this positioned layer UNDER the
 * in-flow buttons, which would otherwise paint beneath it and frost.
 */
export const bottomSheetFooterBlurClass =
  'pointer-events-none absolute inset-0 -z-10 backdrop-blur-[12px] ' +
  '[mask-image:linear-gradient(to_bottom,transparent,black_26px)]';

/**
 * How far the overlay footer may eat into the body when the sheet is height-
 * CONSTRAINED: the footer's 16px top padding plus half of the first 56px
 * button. The body's content region extends this far under the footer's top
 * edge, so scrolled rows dissolve into the blur — but never vanish behind the
 * buttons wholesale.
 */
export const FOOTER_OVERLAP_PX = 44;

/**
 * Bottom padding a scroller's CONTENT needs under an overlay footer so the
 * last row can be scrolled clear of the floating actions. Derivation: the
 * band intrudes 12 + {@link FOOTER_OVERLAP_PX} = 56px into the body; the
 * content slot's own 12px inset absorbs 12 of it, and the standard 12px gap
 * of clearance goes on top → 44 + 12. The native shell applies it to its own
 * scroller automatically; sheets whose slot hosts its OWN list (a
 * `BottomSheetFlatList` with `scroll="list"`) must add it to their
 * `contentContainerStyle`. NOT `scroll="self"`: over a sheet-aware list that
 * stands the content pan down, and gorhom can then never hand the drag back at
 * offset 0 — see `BottomSheetFlatList.native.tsx`.
 */
export const BOTTOM_SHEET_OVERLAY_FOOTER_SCROLL_PAD_PX = FOOTER_OVERLAP_PX + 12;

/**
 * The Surface's overlay footer, laid IN FLOW as the card's last child rather
 * than absolutely pinned. In flow it participates in the card's natural
 * height, so a content-hugging sheet measures body + footer STACKED — content
 * is never hidden behind the actions when there is room to show both. The
 * negative margins splice it into the card's geometry:
 *
 * - `-mb-[12px]` cancels the card's own `pb-[12px]` so the footer sits flush
 *   with the card's bottom edge (matching the absolute version).
 * - `-mt-[12px]` cancels the card's column `gap-[12px]` (rest state — footer
 *   directly after the body, zero overlap).
 * - Constrained (`overlap: true`, the shell's `fill` mode): `-mt-[56px]` =
 *   the 12px gap + {@link FOOTER_OVERLAP_PX}, so the body's tail runs under
 *   the footer by exactly the sanctioned sliver.
 */
export const bottomSheetFooterOverlayInFlowVariants = cva(
  'relative z-10 flex w-full flex-col gap-[10px] px-[12px] pt-[16px] pb-[12px] ' +
    '-mb-[12px] rounded-b-sheet overflow-hidden',
  {
    variants: {
      overlap: {
        true: '-mt-[56px]',
        false: '-mt-[12px]',
      },
    },
    defaultVariants: { overlap: false },
  },
);

export type BottomSheetVariantProps = VariantProps<typeof bottomSheetVariants>;
export type BottomSheetHeaderVariantProps = VariantProps<
  typeof bottomSheetHeaderVariants
>;
export type BottomSheetFooterVariantProps = VariantProps<
  typeof bottomSheetFooterVariants
>;
